/**
 * DashboardRouter — derives dashboard state from the authenticated user's data
 * and renders the appropriate state component (State 1-4).
 *
 * Data bridging: maps the platform's real auth/gamification structures to the
 * DashboardUser shape, falling back to placeholder course data where real
 * course-fetching is handled by deeper pages (e.g. /courses).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useGamification } from '../../hooks/useGamification';
import { useDashboardStats } from '../../hooks/useDashboardStats';
import { SubscriptionTier } from '../../types';
import { courseService } from '../../services/firebase/courses';
import { learningPlansService } from '../../services/firebase/learningPlans';
import { getWeeklyXP } from '../../services/firebase/gamification';
import {
  DashboardUser,
  DashboardCourse,
  DashboardEnrolledCourse,
  DashboardCompletedCourse,
  getDashboardState,
} from './types';
import { DashState1NoAssessment } from './State1_NoAssessment';
import { DashState2AssessmentDone } from './State2_AssessmentDone';
import { DashState3CourseCompleted } from './State3_CourseCompleted';
import { DashState4Premium } from './State4_Premium';

/* ── Placeholder course data (assessment results only — no real source yet) ── */

const PLACEHOLDER_MATCHED_COURSES: DashboardCourse[] = [
  {
    id: 'chatgpt-101',
    title: 'Getting Started with ChatGPT',
    emoji: '🤖',
    durationMin: 25,
    isFree: true,
    category: 'Beginner',
  },
  {
    id: 'ai-small-biz',
    title: 'AI for Small Business Owners',
    emoji: '🏢',
    durationMin: 45,
    isFree: false,
    category: 'Business',
  },
  {
    id: 'ai-writing',
    title: 'Writing & Proposals with AI',
    emoji: '✍️',
    durationMin: 30,
    isFree: false,
    category: 'Productivity',
  },
  {
    id: 'ai-admin',
    title: 'Automating Your Admin',
    emoji: '⚙️',
    durationMin: 35,
    isFree: false,
    category: 'Automation',
  },
];

/* ── Subscription tier helpers ─────────────────────────────────────────────── */

const PAID_TIERS: string[] = [
  SubscriptionTier.STANDARD,
  SubscriptionTier.PREMIUM,
  SubscriptionTier.ANNUAL,
  SubscriptionTier.TWELVE_MONTH_PACKAGE,
  SubscriptionTier.CORPORATE,
  SubscriptionTier.STACKCOMMERCE_LIFETIME,
];

function isPaidTier(tier: string | undefined): boolean {
  return PAID_TIERS.includes(tier ?? '');
}

/* ── Keyframe injection (runs once in the document head) ───────────────────── */

const DASHBOARD_CSS = `
@keyframes dbFadeUp {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
.db-fade-up {
  opacity: 0;
  animation: dbFadeUp 0.45s ease forwards;
}
.btn-amber-outline {
  border: 2px solid #f59e0b;
  color: #f59e0b;
  background: transparent;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}
.btn-amber-outline:hover {
  background: linear-gradient(135deg, #f59e0b, #f97316);
  color: #fff;
  border-color: transparent;
}
`;

function useInjectDashboardCSS() {
  useEffect(() => {
    const id = 'dashboard-animations';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = DASHBOARD_CSS;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);
}

/* ── Main component ─────────────────────────────────────────────────────────── */

export const DashboardRouter: React.FC = () => {
  useInjectDashboardCSS();

  const { user: authUser } = useAuth();
  const { progress: gamification, loading: gamLoading } = useGamification();
  const { stats: dashStats } = useDashboardStats();

  // Real completed courses — blocks dashboard render to ensure correct state determination
  const [completedCourses, setCompletedCourses] = useState<DashboardCompletedCourse[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  // Real enrolled/in-progress courses — loads non-blocking after initial render
  const [enrolledCourses, setEnrolledCourses] = useState<DashboardEnrolledCourse[]>([]);

  // Real weekly XP — non-blocking, defaults to zero array until loaded
  const [weeklyXP, setWeeklyXP] = useState<number[]>([0, 0, 0, 0, 0, 0, 0]);

  // Whether the user completed at least one lesson/module today
  const [completedLessonToday, setCompletedLessonToday] = useState(false);

  useEffect(() => {
    setCoursesLoading(true);
    courseService
      .getCompletedCoursesWithDates()
      .then(data => {
        const mapped: DashboardCompletedCourse[] = data.map(({ course, completedAt }) => ({
          id: course.id,
          title: course.title,
          emoji: '📚',
          durationMin: course.estimatedDuration,
          isFree: course.isFree,
          category: course.category,
          completedAt: completedAt
            ? completedAt.toDate().toLocaleDateString('en-GB')
            : '',
          xpEarned: 0,
        }));
        setCompletedCourses(mapped);
      })
      .catch(() => setCompletedCourses([]))
      .finally(() => setCoursesLoading(false));
  }, [authUser?.uid]);

  useEffect(() => {
    learningPlansService
      .getLearningPlanCourses()
      .then(async likedCourses => {
        const active = likedCourses.filter(
          lc => lc.status !== 'completed' && lc.progress < 100
        );
        const mapped = await Promise.all(
          active.map(async lc => {
            const course = await courseService.getCourse(lc.courseId);
            if (!course) return null;
            return {
              id: lc.courseId,
              title: lc.courseTitle,
              emoji: '📚',
              durationMin: course.estimatedDuration,
              isFree: course.isFree,
              category: course.category,
              progressPct: lc.progress,
            } as DashboardEnrolledCourse;
          })
        );
        setEnrolledCourses(mapped.filter((c): c is DashboardEnrolledCourse => c !== null));
      })
      .catch(() => setEnrolledCourses([]));
  }, [authUser?.uid]);

  // Fetch weekly XP and check if a lesson was completed today.
  // Runs on mount and whenever the gamificationUpdate event fires (e.g. after
  // a lesson or course is completed and XP is written to the database).
  const fetchTodayActivity = useCallback(async (uid: string) => {
    const today = new Date().toDateString();

    const daily = await getWeeklyXP(uid).catch(() => [0, 0, 0, 0, 0, 0, 0] as number[]);
    setWeeklyXP(daily);

    // Check user_progress for any module completed today — more reliable than
    // relying on taskCompletions (which only records milestone XP, not every
    // individual lesson completion).
    try {
      const progressSnap = await getDocs(
        query(collection(db, 'user_progress'), where('userId', '==', uid))
      );
      let lessonDoneToday = false;
      for (const docSnap of progressSnap.docs) {
        const moduleProgress: any[] = docSnap.data().moduleProgress || [];
        for (const mp of moduleProgress) {
          const completedAt = mp.completedAt?.toDate?.();
          if (completedAt && new Date(completedAt).toDateString() === today) {
            lessonDoneToday = true;
            break;
          }
        }
        if (lessonDoneToday) break;
      }
      setCompletedLessonToday(lessonDoneToday);
    } catch {
      setCompletedLessonToday(false);
    }
  }, []);

  useEffect(() => {
    if (!authUser?.uid) return;
    fetchTodayActivity(authUser.uid);
  }, [authUser?.uid, fetchTodayActivity]);

  // Re-fetch today's activity whenever a lesson/XP event is dispatched so the
  // goals update without needing a manual page refresh.
  useEffect(() => {
    if (!authUser?.uid) return;
    const handleUpdate = () => fetchTodayActivity(authUser!.uid);
    window.addEventListener('gamificationUpdate', handleUpdate);
    return () => window.removeEventListener('gamificationUpdate', handleUpdate);
  }, [authUser, fetchTodayActivity]);

  /* Loading — wait for critical data before rendering state components */
  if (gamLoading || coursesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f7f8ff' }}>
        <div className="text-center">
          <div
            className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin mx-auto mb-4"
            style={{ borderColor: '#5b6cff', borderTopColor: 'transparent' }}
          />
          <p className="font-open-sans text-sm text-neutral-500">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  /* Build DashboardUser from real data + placeholders */
  const name = authUser?.firstName ?? authUser?.displayName?.split(' ')[0] ?? 'there';
  const tier = authUser?.subscriptionTier;
  const plan: 'free' | 'premium' = isPaidTier(tier) ? 'premium' : 'free';
  const assessmentCompleted = Boolean(authUser?.skillsAssessmentCompleted);

  const xp = gamification?.totalXP ?? 0;
  const streakDays = dashStats?.currentStreak ?? 0;
  const aiReadinessLevel = gamification?.level?.label ?? 'Improving';
  const aiReadinessPct = gamification?.readinessPercentage ?? 53;

  const dashUser: DashboardUser = {
    name,
    plan,
    assessmentCompleted,
    assessmentResults: {
      role: authUser?.jobTitle ?? 'Small Business Owner',
      experience: 'Beginner',
      topGoal: 'Save time on admin',
      weeklyTime: '1–2 hours',
      matchedCourses: PLACEHOLDER_MATCHED_COURSES,
    },
    enrolledCourses,
    completedCourses,
    xp,
    streakDays,
    aiReadinessLevel,
    aiReadinessPct,
    weeklyXP,
    completedLessonToday,
  };

  const state = getDashboardState(dashUser);

  switch (state) {
    case 1:
      return <DashState1NoAssessment user={dashUser} />;
    case 2:
      return <DashState2AssessmentDone user={dashUser} />;
    case 3:
      return <DashState3CourseCompleted user={dashUser} />;
    case 4:
      return <DashState4Premium user={dashUser} />;
    default:
      return <DashState1NoAssessment user={dashUser} />;
  }
};
